import { Controller } from "@hotwired/stimulus"

// Pill tab switcher with a sliding animated indicator. Tab buttons and
// panels are paired by data-key, same convention as the plain "tabs"
// controller. The indicator's position/width are measured off the active
// tab's own box, so it works with any number of variable-width labels.
export default class extends Controller {
  static targets = ["tab", "panel", "indicator", "list"]

  connect() {
    this.moveIndicator(this.activeTab, false)

    // On a fresh (non-Turbo) page load the webfont often swaps in after this
    // first measurement, reflowing tab widths and leaving the indicator
    // misaligned — re-measure once layout/fonts have actually settled.
    requestAnimationFrame(() => this.moveIndicator(this.activeTab, false))
    document.fonts?.ready?.then(() => this.moveIndicator(this.activeTab, false))

    this.onResize = () => this.moveIndicator(this.activeTab, false)
    window.addEventListener("resize", this.onResize)
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize)
  }

  get activeTab() {
    return this.tabTargets.find((tab) => tab.classList.contains("active")) || this.tabTargets[0]
  }

  select(event) {
    const tab = event.currentTarget
    const key = tab.dataset.key

    this.tabTargets.forEach((t) => t.classList.toggle("active", t === tab))
    this.panelTargets.forEach((panel) => (panel.hidden = panel.dataset.key !== key))
    this.moveIndicator(tab, true)
    this.centerTab(tab)
  }

  moveIndicator(tab, animate) {
    if (!tab || !this.hasIndicatorTarget) return

    this.indicatorTarget.style.transition = animate ? "" : "none"
    this.indicatorTarget.style.width = `${tab.offsetWidth}px`
    this.indicatorTarget.style.transform = `translateX(${tab.offsetLeft}px)`

    if (!animate) this.indicatorTarget.offsetHeight // force reflow so the next transition re-enables cleanly
  }

  // Scrolls the tab strip (not the page) so the selected tab sits in the
  // middle of the strip's *visible* window — on narrow screens most of the
  // strip is offscreen, and centering the pick surfaces its neighbors
  // instead of leaving them hidden past the edge the way scrollIntoView's
  // "nearest" would.
  centerTab(tab) {
    if (!this.hasListTarget) return

    const list = this.listTarget
    // tab.offsetLeft is measured in the same coordinate space as scrollLeft
    // here — the strip is its own offsetParent (position:relative), so this
    // already accounts for text direction on its own. Only the valid
    // scrollLeft *range* flips in RTL: browsers implementing the standard
    // ("negative") model run from 0 (the start/rightmost position) down to
    // -max (fully scrolled to the visual left), the mirror of LTR's [0, max].
    const target = tab.offsetLeft + tab.offsetWidth / 2 - list.clientWidth / 2
    const max = list.scrollWidth - list.clientWidth
    const isRTL = getComputedStyle(list).direction === "rtl"
    const clamped = isRTL ? Math.max(-max, Math.min(target, 0)) : Math.max(0, Math.min(target, max))

    list.scrollTo({ left: clamped, behavior: "smooth" })
  }
}
